import React from "react";
import {
    Dialog,
    Pane,
    Strong,
    Text,
    Icon,
    Button,
    Heading,
    Paragraph,
    Card,
    Badge
} from "evergreen-ui";
import { connect } from "react-redux";

const GuestContactSupportDialogContent = (props) => {
    const { showContactSupportDialog, onCloseComplete } = props;

    // Define color codes for consistent use
    const colors = {
        whatsapp: {
            bg: "#E5F7F1",
            border: "#25D366",
            text: "#075E54",
            icon: "#25D366"
        },
        email: {
            bg: "#FFF9DB",
            border: "#EEDC87",
            text: "#776921",
            icon: "#9B51E0"
        },
        phone: {
            bg: "#E6F0FF",
            border: "#4285F4",
            text: "#174EA6",
            icon: "#4285F4"
        },
        ticket: {
            bg: "#E3F5EB",
            border: "#34A853",
            text: "#137333",
            icon: "#34A853"
        }
    };

    return (
        <Dialog
            isShown={showContactSupportDialog}
            title="Contact Playo Partner Support"
            onCloseComplete={onCloseComplete}
            hasFooter={false}
            width={600}
            containerProps={{
                maxHeight: "85vh",
                overflow: "auto"
            }}
        >
            <Pane marginBottom={16}>
                <Paragraph size={400} color="muted">
                    Our support team is available to help you with any questions or issues.
                    Choose your preferred contact method below:
                </Paragraph>
            </Pane>

            {/* WhatsApp */}
            <Card
                elevation={0}
                backgroundColor="tint1"
                marginBottom={16}
                borderRadius={4}
                border
                borderColor={colors.whatsapp.border}
            >
                <Pane
                    background={colors.whatsapp.bg}
                    padding={12}
                    borderTopLeftRadius={4}
                    borderTopRightRadius={4}
                    borderBottom="1px solid #EDF0F2"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Pane display="flex" alignItems="center">
                        <img
                            width={"18px"}
                            height={"18px"}
                            src="https://playo-website.gumlet.io/venues-listing/socail/whatsapp.svg?w=50&h=50"
                            alt="WhatsApp icon"
                            style={{ marginRight: "10px" }}
                        />
                        <Strong size={500} color={colors.whatsapp.text}>WhatsApp Support</Strong>
                    </Pane>
                    <Badge color="green">Preferred</Badge>
                </Pane>
                <Pane padding={16}>
                    <Text size={300} color="muted" marginBottom={8} display="block">
                        Get quick support through WhatsApp for urgent issues and quick questions
                    </Text>
                    <Pane display="flex" alignItems="center">
                        <Strong
                            is="a"
                            href="https://wa.me/917676447433"
                            target="_blank"
                            rel="noopener noreferrer"
                            color={colors.whatsapp.text}
                            textDecoration="underline"
                            className="no-underline"
                        >
                            +91 7676447433
                        </Strong>
                    </Pane>
                </Pane>
            </Card>

            {/* Email Support */}
            <Card
                elevation={0}
                backgroundColor="tint1"
                marginBottom={16}
                borderRadius={4}
                border
                borderColor={colors.email.border}
            >
                <Pane
                    background={colors.email.bg}
                    padding={12}
                    borderTopLeftRadius={4}
                    borderTopRightRadius={4}
                    borderBottom="1px solid #EDF0F2"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Pane display="flex" alignItems="center">
                        <Strong size={500} color={colors.email.text}>Email Support</Strong>
                    </Pane>
                </Pane>
                <Pane padding={16}>
                    <Text size={300} color="muted" marginBottom={8} display="block">
                        Send us an email with details about your query. We typically respond within 1 business day.
                    </Text>
                    <Pane display="flex" alignItems="center">
                        <Strong
                            is="a"
                            href="mailto:partner.support@playo.co"
                            color={colors.email.text}
                            textDecoration="underline"
                            className="no-underline"
                        >
                            partner.support@playo.co
                        </Strong>
                    </Pane>
                </Pane>
            </Card>

            {/* Phone Support */}
            <Card
                elevation={0}
                backgroundColor="tint1"
                marginBottom={16}
                borderRadius={4}
                border
                borderColor={colors.phone.border}
            >
                <Pane
                    background={colors.phone.bg}
                    padding={12}
                    borderTopLeftRadius={4}
                    borderTopRightRadius={4}
                    borderBottom="1px solid #EDF0F2"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Pane display="flex" alignItems="center">
                        <Strong size={500} color={colors.phone.text}>Phone Number</Strong>
                    </Pane>
                </Pane>
                <Pane padding={16}>
                    <Text size={300} color="muted" marginBottom={8} display="block">
                        Speak directly with our support team during business hours (10 AM - 10 PM IST, All Days)
                    </Text>
                    <Pane display="flex" alignItems="center">
                        <Strong
                            is="a"
                            href="tel:+919513253380"
                            color={colors.phone.text}
                            textDecoration="underline"
                            className="no-underline"
                        >
                            +91 9513253380
                        </Strong>
                    </Pane>
                </Pane>
            </Card>

            {/* Support Ticket */}
            <Card
                elevation={0}
                backgroundColor="tint1"
                marginBottom={16}
                borderRadius={4}
                border
                borderColor={colors.ticket.border}
            >
                <Pane
                    background={colors.ticket.bg}
                    padding={12}
                    borderTopLeftRadius={4}
                    borderTopRightRadius={4}
                    borderBottom="1px solid #EDF0F2"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Pane display="flex" alignItems="center">
                        <Strong size={500} color={colors.ticket.text}>Support Ticket</Strong>
                    </Pane>
                </Pane>
                <Pane padding={16}>
                    <Text size={300} color="muted" marginBottom={8} display="block">
                        Create a support ticket for detailed assistance. Our team will respond within 24 hours.
                    </Text>
                    <Pane display="flex" alignItems="center" marginBottom={8}>
                        <Strong
                            is="a"
                            href="https://playo-partner.freshdesk.com/support/tickets/new"
                            target="_blank"
                            rel="noopener noreferrer"
                            color={colors.ticket.text}
                            textDecoration="underline"
                            className="no-underline"
                        >
                            Create Support Ticket
                        </Strong>
                    </Pane>
                    <Text size={300} color="muted" fontStyle="italic">
                        Best for detailed technical issues or account-related queries
                    </Text>
                </Pane>
            </Card>

            {/* Footer */}
            <Pane
                marginTop={24}
                marginBottom={12}
                padding={16}
                background="#F9FAFB"
                borderRadius={4}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                border
                borderColor="#E4E7EB"
            >
                <Pane display="flex" alignItems="center" marginBottom={8}>
                    <Badge color="neutral" marginRight={8}>Business Hours</Badge>
                    <Text size={300} color="muted">
                        All Days, 10 AM - 10 PM IST
                    </Text>
                </Pane>
                <Text size={300} color="muted" fontStyle="italic">
                    We strive to provide the fastest possible resolution to all your queries
                </Text>
            </Pane>
        </Dialog>
    );
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(GuestContactSupportDialogContent);